import axios from 'axios';

// Production API — plain http by decision (the backend has no TLS). The site
// must therefore be served over http too; an https page can't call an http API.
const DEFAULT_API_URL = 'http://services.smartifyindia.co.in:8085/api';

// ── API base URL ─────────────────────────────────────────────────────────────
// In a DEV browser (npm start, not the APK) talk to the API on the SAME host the
// page is served from — so opening localhost:3300 hits localhost:8085 and opening
// 192.168.x:3300 (a phone on the LAN) hits 192.168.x:8085. This avoids the
// cross-origin "private network" block you get when a localhost page calls a LAN
// IP. The APK and any production build keep using the baked REACT_APP_API_URL.
function resolveBaseURL() {
  const configured = process.env.REACT_APP_API_URL;
  const isNative = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
  // Dev against a LOCAL backend (REACT_APP_API_URL unset or on localhost): use the
  // page's own host, so a phone on the LAN (192.168.x.x:3300) reaches this PC's
  // API too. When .env.development names a real server, call that server as-is.
  const localApi = !configured || /\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(configured);
  if (process.env.NODE_ENV === 'development' && localApi && !isNative && typeof window !== 'undefined' && window.location && window.location.hostname) {
    return `${window.location.protocol}//${window.location.hostname}:8085/api`;
  }
  // An https PAGE may not call an http API - the browser blocks it as mixed
  // content and every request fails with "check your connection". Phones keep
  // reaching the site over https (a cached permanent redirect, or Chrome's
  // https-first upgrade), so when the page itself is https we talk to /api on
  // the SAME origin; Apache proxies that to the plain-http backend. Over http
  // the app keeps calling the backend directly.
  if (!isNative && typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
    return `${window.location.origin}/api`;
  }
  return configured || DEFAULT_API_URL;
}

// ── Axios instance (Basic auth, mirrors SharpAccounting's SmartifyAPI) ───────
const encoded = btoa('admin:134');
const SmartifyAPI = axios.create({
  baseURL: resolveBaseURL(),
  headers: { Authorization: `Basic ${encoded}` },
});

// ── System-wide PascalCase request interceptor (name-like fields only) ───────
const PASCAL_SUFFIXES = [
  'Name', 'Surname', 'FatherHusband', 'Add1', 'Add2', 'Add3',
  'City', 'District', 'Designation', 'Role', 'Relation', 'GroupName',
  'Venue', 'Category', 'ItemName', 'Title', 'EventRole',
];
const DENY_KEYS = new Set([
  'Password', 'OrganiserCode', 'EmailId', 'MobileNumber', 'WhatsAppNumber',
  'Pincode', 'GpsLocation', 'Logo', 'Reference', 'PaymentMode', 'InvitedVia',
  'NotifyVia', 'Status', 'Priority', 'InviteStatus', 'RSVPStatus', 'Side',
  'PaymentType', 'EventType', 'Description', 'Remarks', 'Notes', 'Address', 'where',
  'Visibility', 'DeviceName', 'DeviceModel', // device ids / models are verbatim
  // A synced phone book is stored exactly as the phone holds it.
  'DisplayName', 'GivenName', 'MiddleName', 'FamilyName', 'Organization', 'NumberLabel',
]);

const isPascalKey = (key) => {
  if (!key) return false;
  if (DENY_KEYS.has(key)) return false;
  const lower = key.toLowerCase();
  if (lower.endsWith('id') || lower.endsWith('number') || lower.endsWith('code')) return false;
  if (lower.endsWith('date') || lower.endsWith('datetime')) return false;
  return PASCAL_SUFFIXES.some(s => lower.endsWith(s.toLowerCase()));
};
const toPascalLocal = (str) => {
  if (typeof str !== 'string' || !str.trim()) return str;
  return str.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};
const isBinaryBody = (v) => {
  if (!v) return false;
  if (typeof FormData !== 'undefined' && v instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && v instanceof Blob) return true;
  if (typeof File !== 'undefined' && v instanceof File) return true;
  if (typeof ArrayBuffer !== 'undefined' && v instanceof ArrayBuffer) return true;
  if (typeof URLSearchParams !== 'undefined' && v instanceof URLSearchParams) return true;
  return false;
};
const normalisePayload = (value, key) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(v => normalisePayload(v));
  if (typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      if (k === 'where') { out[k] = value[k]; continue; }
      out[k] = normalisePayload(value[k], k);
    }
    return out;
  }
  if (typeof value === 'string' && key && isPascalKey(key)) return toPascalLocal(value);
  return value;
};
SmartifyAPI.interceptors.request.use(req => {
  if (req.data && typeof req.data === 'object' && !isBinaryBody(req.data)) {
    req.data = normalisePayload(req.data);
  }
  return req;
});

const post = (path, payload) => SmartifyAPI.post(path, payload).then(r => r.data);

// ── Auth / Users ─────────────────────────────────────────────────────────────
export const getUserDetails = (p) => post('/GetUserbyMobileNumber', p);
export const GetUserbyUserID = (p) => post('/GetUserbyUserID', p);
export const GetAllUsers = (p = {}) => post('/GetAllUsers', p);
export const AddUsers = (p) => post('/AddUsers', p);
export const UpdateUser = (p) => post('/UpdateUsers', p);
export const ClearUserDevice = (p) => post('/ClearUserDevice', p);
export const VerifyUserDevice = (p) => post('/VerifyUserDevice', p); // { MobileNumber, Password, DeviceId, DeviceToken, Platform, Bind?, DeviceModel? } → may return deviceToken
export const BindUserDevice = (p) => post('/BindUserDevice', p);     // after OTP: { MobileNumber, DeviceId, Platform, DeviceModel? } → { deviceToken }
export const ResetPasswordWithOtp = (p) => post('/ResetPasswordWithOtp', p); // forgot password, after a verified OTP: { MobileNumber, Password } → { ok, reason?, user? }
// Sign-in routing for one event: { MobileNumber, EventId } → { kind: 'staff'|'guest'|'inactive'|'not_found', name }
export const IdentifyEventUser = (p) => post('/IdentifyEventUser', p);
// Guest sign-in after a verified OTP: { MobileNumber, EventId } → { ok, reason?, user, event, side }
export const GuestSignIn = (p) => post('/GuestSignIn', p);
export const UploadInviteFileToServer = async (file, OrganiserId, EventId, GuestId, Kind) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('OrganiserId', OrganiserId);
  formData.append('EventId', EventId);
  formData.append('GuestId', GuestId);
  formData.append('Kind', Kind);
  const response = await SmartifyAPI.post('/UploadInviteFileToServer', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};
export const getOrganisersByUser = (p) => post('/GetAllOrganiserDetailsByUserId', p);
export const GetAllUsersByOrganiserId = (p) => post('/GetAllUsersByOrganiserId', p);
export const GetAllUsersNotAllotedByOrganiserId = (p) => post('/GetAllUsersNotAllotedByOrganiserId', p);

// ── OTP ───────────────────────────────────────────────────────────────────
// Meta bills each OTP, so the API re-sends only after the previous code expired
// (1 hour). The body is a JSON *string* array; resolve to the first row so the UI
// can show { reused: true, Date_Time } = "use the code we already sent".
export const sendOtp = (p) => post('/SendOtpOnWhatsAppbyMobileNumber', p).then(r => {
  const rows = typeof r === 'string' ? JSON.parse(r) : r;
  return Array.isArray(rows) ? rows[0] || {} : rows || {};
});
export const verifyOtp = (p) => post('/UpdateOtpVerification', p);

// ── Organiser (tenant) ──────────────────────────────────────────────────────
export const GetAllEvt_Organiser = (p = {}) => post('/GetAllEvt_Organiser', p);
export const GetEvt_OrganiserById = (p) => post('/GetEvt_OrganiserById', p);
export const AddEvt_Organiser = (p) => post('/AddEvt_Organiser', p);
export const UpdateEvt_Organiser = (p) => post('/UpdateEvt_Organiser', p);
export const DeleteEvt_Organiser = (p) => post('/DeleteEvt_Organiser', p);
export const GetAllEventTypes = (p = {}) => post('/GetAllEvt_EventTypes', p);
export const GetAllFunctionTypes = (p = {}) => post('/GetAllEvt_FunctionTypes', p);
export const GetFunctionTypesByEventTypeId = (p) => post('/GetAllEvt_FunctionTypesByEventTypeId', p);

// ── User ↔ Organiser mapping ────────────────────────────────────────────────
export const AddEvt_UserOrganiserMapping = (p) => post('/AddEvt_UserOrganiserMapping', p);
export const UpdateEvt_UserOrganiserMapping = (p) => post('/UpdateEvt_UserOrganiserMapping', p);
export const DeleteEvt_UserOrganiserMapping = (p) => post('/DeleteEvt_UserOrganiserMapping', p);

// ── Events ──────────────────────────────────────────────────────────────────
export const GetAllEventsByOrganiserId = (p) => post('/GetAllEvt_EventsByOrganiserId', p);
export const GetAllEventsByUserId = (p) => post('/GetAllEvt_EventsByUserId', p);
export const GetEventById = (p) => post('/GetEvt_EventsById', p);
export const AddEvent = (p) => post('/AddEvt_Events', p);
export const UpdateEvent = (p) => post('/UpdateEvt_Events', p);
export const DeleteEvent = (p) => post('/DeleteEvt_Events', p);

// ── Team (UserEventsMapping) ─────────────────────────────────────────────────
export const GetTeamByEventId = (p) => post('/GetAllEvt_TeamByEventId', p);
export const GetUsersNotInEvent = (p) => post('/GetAllUsersNotInEvent', p);
export const AddTeamMember = (p) => post('/AddEvt_UserEventsMapping', p);
export const UpdateTeamMember = (p) => post('/UpdateEvt_UserEventsMapping', p);
export const DeleteTeamMember = (p) => post('/DeleteEvt_UserEventsMapping', p);
// The signed-in user's per-event permission row (event-scoped permission source).
export const GetUserEventPermissions = (p) => post('/GetEvt_UserEventsMappingByEventIdAndUserId', p);

// ── User activity log (login/logout + 1-min heartbeat) ───────────────────────
// AddUserLog returns { insertId } = the session's LogId. Each ping calls
// UpdateUserLog to bump LogOutTime on that row (last ping = effective logout).
export const AddUserLog = (p) => post('/AddEvt_UserLog', p);
export const UpdateUserLog = (p) => post('/UpdateEvt_UserLog', p);
export const GetUserLogByUserId = (p) => post('/GetEvt_UserLogByUserId', p);
export const GetUserLogLatest = (p = {}) => post('/GetEvt_UserLogLatest', p);

// ── Functions (sub-events of an Event) ───────────────────────────────────────
export const GetFunctionsByEventId = (p) => post('/GetAllEvt_FunctionsByEventId', p);
export const AddFunction = (p) => post('/AddEvt_Functions', p);
export const UpdateFunction = (p) => post('/UpdateEvt_Functions', p);
export const DeleteFunction = (p) => post('/DeleteEvt_Functions', p);

// ── Guests / Members ─────────────────────────────────────────────────────────
export const GetGuestsByEventId = (p) => post('/GetAllEvt_GuestsByEventId', p);
export const GetGuestsByEventIdForMember = (p) => post('/GetAllEvt_GuestsByEventIdForMember', p);
export const GetGuestsByEventIdAndCreatedBy = (p) => post('/GetAllEvt_GuestsByEventIdAndCreatedBy', p);
export const GetGuestsByEventIdAndMobile = (p) => post('/GetEvt_GuestsByEventIdAndMobile', p);
// The QR code printed on an invitation label: one guest by their public id.
export const GetGuestByUuid = (p) => post('/GetEvt_GuestByUuid', p);        // { Uuid }
export const AddGuest = (p) => post('/AddEvt_Guests', p);
export const AddGuestToFunctions = (p) => post('/AddEvt_GuestsToFunctions', p);
export const SetGuestFunctions = (p) => post('/SetEvt_GuestFunctions', p);
// Already invited? { EventId, FunctionIds, MobileNumber?, Surname?, GuestName, ExceptGuestId? } → [{ GuestId, GuestName, Surname, FunctionNames, AddedBy }]
export const CheckGuestDuplicate = (p) => post('/CheckEvt_GuestDuplicate', p);
// Find a person while adding a guest: guests of this organiser's events + my synced phone book.
// { OrganiserId, UserId, Text, AllGuests } → { guests: [...], contacts: [...] }
export const SearchPeople = (p) => post('/SearchEvt_People', p);

// ── Invitation card kinds (Reception card, Thread Ceremony insert …) ─────────
export const GetCardTypes = (p) => post('/GetAllEvt_CardTypesByEventId', p);   // { EventId }
export const AddCardType = (p) => post('/AddEvt_CardTypes', p);
export const UpdateCardType = (p) => post('/UpdateEvt_CardTypes', p);          // { data, where } — delete = Valid 0
// ── Expenses + their payments ──────────────────────────────────────────────
export const GetExpenses = (p) => post('/GetAllEvt_ExpensesByEventId', p);     // { EventId } → rows + PaidAmount
export const AddExpense = (p) => post('/AddEvt_Expenses', p);
export const UpdateExpense = (p) => post('/UpdateEvt_Expenses', p);            // { data, where } — delete = Valid 0
export const GetExpensePayments = (p) => post('/GetAllEvt_ExpensePaymentsByEventId', p); // { EventId }
export const AddExpensePayment = (p) => post('/AddEvt_ExpensePayments', p);
export const UpdateExpensePayment = (p) => post('/UpdateEvt_ExpensePayments', p);
// The lines one expense / service is made of (utils/costLines.js) — delete = Valid 0.
export const GetCostLines = (p) => post('/GetAllEvt_CostLinesByEventId', p);       // { EventId }
export const AddCostLine = (p) => post('/AddEvt_CostLines', p);
export const UpdateCostLine = (p) => post('/UpdateEvt_CostLines', p);             // { data, where }
// Synced phone book (Profile → Sync my contacts) — only after the in-app consent.
export const SyncUserContacts = (p) => post('/SyncEvt_UserContacts', p);       // { UserId, Consent:1, Replace?, Contacts:[...] }
export const GetUserContactsStatus = (p) => post('/GetEvt_UserContactsStatus', p); // { UserId } → { contacts, numbers, syncedDate, consentDate }
export const GetUserContactsHashes = (p) => post('/GetEvt_UserContactsHashes', p); // { UserId } → { hashes: { ContactKey: fingerprint } }
// The phone's ★ default number per contact — only updates contacts already saved.
export const SetUserContactsPrimary = (p) => post('/SetEvt_UserContactsPrimary', p); // { UserId, Consent:1, Primary:[{ ContactKey, Number }] }
export const DeleteUserContacts = (p) => post('/DeleteEvt_UserContacts', p);   // { UserId } → { ok, deleted }
export const AddOrMoveFamilyMember = (p) => post('/AddOrMoveEvt_FamilyMember', p);
export const UpdateGuest = (p) => post('/UpdateEvt_Guests', p);
export const DeleteGuest = (p) => post('/DeleteEvt_Guests', p);

// ── Guest self-service (invitation page: login by mobile + OTP, then RSVP) ────
// All invitations for a mobile across every event/function, with venue + GPS.
export const GetGuestInvitesByMobile = (p) => post('/GetEvt_GuestInvitesByMobile', p);
// RSVP for one invitation. Scoped by GuestId + the guest's own mobile for safety.
export const SubmitGuestRsvp = ({ GuestId, MobileNumber, RSVPStatus, ConfirmedCount }) =>
  UpdateGuest({
    data: { RSVPStatus, ConfirmedCount, InviteStatus: rsvpToInvite(RSVPStatus) },
    where: `GuestId = ${GuestId} and MobileNumber = '${MobileNumber}'`,
  });
// Map an RSVP answer onto the InviteStatus board column so admins see it move.
const rsvpToInvite = (rsvp) =>
  rsvp === 'Yes' ? 'Confirmed' : rsvp === 'No' ? 'Declined' : 'Invited';

// ── Guest party members (people in a guest's party) ──────────────────────────
export const GetGuestMembersByGuestId = (p) => post('/GetAllEvt_GuestMembersByGuestId', p);
export const GetGuestMembersByEventId = (p) => post('/GetAllEvt_GuestMembersByEventId', p);
export const AddGuestMember = (p) => post('/AddEvt_GuestMembers', p);
export const UpdateGuestMember = (p) => post('/UpdateEvt_GuestMembers', p);
export const DeleteGuestMember = (p) => post('/DeleteEvt_GuestMembers', p);

// ── Vendors ───────────────────────────────────────────────────────────────

// ── Arrangements ────────────────────────────────────────────────────────────

// ── Payments ──────────────────────────────────────────────────────────────

// ── Reminders ───────────────────────────────────────────────────────────────

// ── Tasks ───────────────────────────────────────────────────────────────────

// ── Attendance / RSVP ───────────────────────────────────────────────────────

export const GetInvitationRemindersByEventId = (p) => post('/GetAllEvt_InvitationRemindersByEventId', p);
export const AddInvitationReminder = (p) => post('/AddEvt_InvitationReminder', p);
export const UpdateInvitationReminder = (p) => post('/UpdateEvt_InvitationReminder', p);
export const GetInvitationChecklist = (p) => post('/GetEvt_InvitationChecklist', p);
export const SetInvitationCallStatus = (p) => post('/SetEvt_InvitationCallStatus', p);

// ── Services (catering, event management, DJ, priest, … — one generic module) ─
// Masters: service types, and the item catalogue of a type (food dishes, jobs).
export const GetServiceTypes = (p = {}) => post('/GetAllEvt_ServiceTypes', p);
export const GetServiceItemTypes = (p) => post('/GetAllEvt_ServiceItemTypes', p);
// Engagements of an event / of one vendor, plus the per-type money summary.
export const GetServicesByEventId = (p) => post('/GetAllEvt_ServicesByEventId', p);
export const GetServiceById = (p) => post('/GetEvt_ServiceById', p);
export const GetServicesByVendor = (p) => post('/GetAllEvt_ServicesByVendorUserId', p);
export const GetServiceSummary = (p) => post('/GetEvt_ServiceSummaryByEventId', p);
export const AddService = (p) => post('/AddEvt_Services', p);
export const UpdateService = (p) => post('/UpdateEvt_Services', p);
export const DeleteService = (p) => post('/DeleteEvt_Services', p);
// Lines of one engagement: menu dishes, job list, songs …
export const GetServiceItems = (p) => post('/GetAllEvt_ServiceItemsByServiceId', p);
export const AddServiceItem = (p) => post('/AddEvt_ServiceItems', p);
export const UpdateServiceItem = (p) => post('/UpdateEvt_ServiceItems', p);
export const DeleteServiceItem = (p) => post('/DeleteEvt_ServiceItems', p);
// Advance + instalments (the API re-totals the service after every change).
export const GetServicePayments = (p) => post('/GetAllEvt_ServicePaymentsByServiceId', p);
export const AddServicePayment = (p) => post('/AddEvt_ServicePayments', p);
export const UpdateServicePayment = (p) => post('/UpdateEvt_ServicePayments', p);
export const DeleteServicePayment = (p) => post('/DeleteEvt_ServicePayments', p);

export default SmartifyAPI;
